import { useState, useEffect, memo } from "react";
import {
  Grid,
  TextField,
  Button,
  Stack,
  Box,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  Chip,
  Typography,
  Divider,
  InputAdornment,
  IconButton,
  Tooltip,
  Alert,
} from "@mui/material";
import { generateRandomToken } from "../../utils/functions.js";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";

const Prestashop = ({ ecommerce, warehouses, onSave, loading, isEdit }) => {
  const [formData, setFormData] = useState(ecommerce);
  const [deliveryInput, setDeliveryInput] = useState("");
  const [copiedTooltip, setCopiedTooltip] = useState("");

  useEffect(() => {
    if (!formData.configuration.authentication.token) {
      const newToken = generateRandomToken();
      setFormData((prev) => ({
        ...prev,
        configuration: {
          ...prev.configuration,
          authentication: {
            ...prev.configuration.authentication,
            token: newToken,
          },
        },
      }));
    }
  }, []);

  const handleChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      configuration: {
        ...prev.configuration,
        authentication: {
          ...prev.configuration.authentication,
          [field]: value,
        },
      },
    }));
  };

  const handleWarehouseChange = (e) => {
    setFormData({ ...formData, warehouse: e.target.value });
  };

  const addDeliveryMethod = () => {
    if (!deliveryInput) return;
    const currentMethods =
      formData.configuration.options.enabled_delivery_methods || [];
    if (!currentMethods.includes(deliveryInput)) {
      setFormData((prev) => ({
        ...prev,
        configuration: {
          ...prev.configuration,
          options: {
            ...prev.configuration.options,
            enabled_delivery_methods: [...currentMethods, deliveryInput],
          },
        },
      }));
    }
    setDeliveryInput("");
  };

  const removeDeliveryMethod = (method) => {
    const currentMethods =
      formData.configuration.options.enabled_delivery_methods.filter(
        (m) => m !== method,
      );
    setFormData((prev) => ({
      ...prev,
      configuration: {
        ...prev.configuration,
        options: {
          ...prev.configuration.options,
          enabled_delivery_methods: currentMethods,
        },
      },
    }));
  };

  const handleCopy = (text, fieldName) => {
    if (!text) return;
    navigator.clipboard.writeText(text).then(() => {
      setCopiedTooltip(fieldName);
      setTimeout(() => setCopiedTooltip(""), 2000);
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
  };

  return (
    <form>
      <h3 style={{ marginBottom: "20px" }}>Configuración PrestaShop</h3>

      <Alert severity="info" sx={{ mb: 3 }}>
        Configura los datos de tu tienda y copia el Token de seguridad y la URL
        del Webhook para pegarlos en tu módulo de PrestaShop.
      </Alert>

      <FormControl fullWidth sx={{ mb: 3 }}>
        <InputLabel>Bodega Asociada</InputLabel>
        <Select
          value={formData.warehouse || ""}
          label="Bodega Asociada"
          onChange={handleWarehouseChange}
        >
          {warehouses.map((w) => (
            <MenuItem key={w.id} value={w.id}>
              {w.name}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12}>
          <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: "bold" }}>
            Credenciales API
          </Typography>
          <TextField
            fullWidth
            label="Nombre de la Tienda"
            value={formData.configuration.authentication.shop_name || ""}
            onChange={(e) => handleChange("shop_name", e.target.value)}
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            fullWidth
            label="URL de la Tienda"
            placeholder="https://mitienda.com"
            value={formData.configuration.authentication.shop_url || ""}
            onChange={(e) => handleChange("shop_url", e.target.value)}
          />
        </Grid>
      </Grid>

      <Divider sx={{ my: 3 }} />

      <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: "bold" }}>
        Configuración del Módulo (Copiar a PrestaShop)
      </Typography>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Token de Seguridad"
            value={formData.configuration.authentication.token || ""}
            disabled
            helperText="Copia este token y pégalo en la configuración del módulo en PrestaShop"
            InputProps={{
              readOnly: true,
              endAdornment: (
                <InputAdornment position="end">
                  <Tooltip
                    title={
                      copiedTooltip === "token" ? "¡Copiado!" : "Copiar Token"
                    }
                    arrow
                    open={copiedTooltip === "token" || undefined}
                  >
                    <IconButton
                      onClick={() =>
                        handleCopy(
                          formData.configuration.authentication.token,
                          "token",
                        )
                      }
                    >
                      <ContentCopyIcon />
                    </IconButton>
                  </Tooltip>
                </InputAdornment>
              ),
            }}
          />
        </Grid>

        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Webhook URL (Crear Orden)"
            value={
              formData.configuration.create_order?.url ||
              "Guarda la integración para generar la URL"
            }
            disabled
            InputProps={{
              readOnly: true,
              endAdornment: (
                <InputAdornment position="end">
                  <Tooltip
                    title={
                      copiedTooltip === "webhook"
                        ? "¡Copiado!"
                        : "Copiar Webhook"
                    }
                    arrow
                    open={copiedTooltip === "webhook" || undefined}
                  >
                    <IconButton
                      onClick={() =>
                        handleCopy(
                          formData.configuration.create_order?.url,
                          "webhook",
                        )
                      }
                      disabled={!formData.configuration.create_order?.url}
                    >
                      <ContentCopyIcon />
                    </IconButton>
                  </Tooltip>
                </InputAdornment>
              ),
            }}
          />
        </Grid>
      </Grid>

      <Box
        sx={{
          mt: 4,
          mb: 3,
          border: "1px solid #e0e0e0",
          p: 2,
          borderRadius: 2,
        }}
      >
        <InputLabel sx={{ mb: 1 }}>Métodos de Envío Habilitados</InputLabel>
        <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
          <TextField
            size="small"
            placeholder="Ej: Starken, Chilexpress..."
            value={deliveryInput}
            onChange={(e) => setDeliveryInput(e.target.value)}
            fullWidth
          />
          <Button
            variant="contained"
            onClick={addDeliveryMethod}
            disabled={!deliveryInput}
            sx={{ marginTop: "30px !important" }}
          >
            Agregar
          </Button>
        </Stack>
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          {formData.configuration.options.enabled_delivery_methods?.length >
          0 ? (
            formData.configuration.options.enabled_delivery_methods.map((m) => (
              <Chip
                key={m}
                label={m}
                onDelete={() => removeDeliveryMethod(m)}
                color="primary"
                variant="outlined"
              />
            ))
          ) : (
            <span style={{ color: "#999", fontSize: "0.9rem" }}>
              No hay métodos configurados
            </span>
          )}
        </Stack>
      </Box>

      <Button
        type="submit"
        variant="contained"
        fullWidth
        disabled={loading}
        size="large"
        onClick={handleSubmit}
      >
        {isEdit ? "Actualizar Integración" : "Guardar Integración"}
      </Button>
    </form>
  );
};

export default memo(Prestashop);
